import { getAllPermissions, getAllQueue, GetQueue ,addPortalSettings , getPortalSettings} from "./HeedifyServices";

/**
 * Récupère les scopes et queues accessibles par l'utilisateur en fonction de ses permissions
 * @param {string} tenantId - ID du tenant
 * @param {string} userId - ID de l'utilisateur
 * @param {string} token - Token d'authentification
 * @returns {Promise<{scopes: string[], queues: Object}>}
 */
export async function getUserAccessibleQueues(tenantId, userId, token) {
  try {
    // 1. Récupérer les permissions de l'utilisateur
    const permissions = await getAllPermissions(tenantId, token);
    
    if (!permissions || !permissions.scopes) {
      return { scopes: [], queues: {} };
    }

    const scopeIds = Object.keys(permissions.scopes);
    const allQueuesData = {};
    const allQueues = [];

    // 2. Pour chaque scope accessible, récupérer les queues
    for (const scopeId of scopeIds) {
      try {
        const scopeQueues = await getAllQueue(tenantId, userId, scopeId, token);
        
        if (Array.isArray(scopeQueues)) {
          // Ajouter le scopeId à chaque queue
          const queuesWithScope = scopeQueues.map(queue => ({
            ...queue,
            scope: scopeId
          }));
          
          allQueuesData[scopeId] = queuesWithScope;
          allQueues.push(...queuesWithScope);
        }
      } catch (error) {
        console.error(`Error fetching queues for scope ${scopeId}:`, error);
        allQueuesData[scopeId] = [];
      }
    }

    // 3. Ajouter l'option "All" qui contient toutes les queues
    allQueuesData["All"] = allQueues;

    return {
      scopes: ["All", ...scopeIds],
      queues: allQueuesData,
      permissions
    };
  } catch (error) {
    console.error("Error in getUserAccessibleQueues:", error);
    return { scopes: [], queues: {}, permissions: null };
  }
}

/**
 * Récupère les KPI initiaux pour une liste de queues
 * @param {string} tenantId - ID du tenant
 * @param {string} userId - ID de l'utilisateur
 * @param {Array} queues - Liste des queues {id, scope}
 * @param {string} token - Token d'authentification
 * @returns {Promise<Object>} - Objet avec les KPI par queueId
 */
export async function getQueuesKpis(tenantId, userId, queues, token) {
  try {
    const kpisPromises = queues.map(queue => 
      GetQueue(tenantId, userId, queue.id, queue.scope, token)
        .then(data => ({ queueId: queue.id, data }))
        .catch(error => {
          console.error(`Error fetching KPI for queue ${queue.id}:`, error);
          return { queueId: queue.id, data: null };
        })
    );

    const results = await Promise.all(kpisPromises);
    
    // Convertir le tableau en objet {queueId: data}
    return results.reduce((acc, { queueId, data }) => {
      if (data) {
        acc[queueId] = parseQueueKpi(data);
      }
      return acc;
    }, {});
  } catch (error) {
    console.error("Error in getQueuesKpis:", error);
    return {};
  }
}

/**
 * Parse les données d'une queue pour extraire les KPI dans un format standardisé
 * @param {Object} queueData - Données brutes de la queue
 * @returns {Object} - KPI formatés
 */
export function parseQueueKpi(queueData) {
  if (!queueData) return null;
  
  // 📅 Date locale d'aujourd'hui au format YYYY-MM-DD
  const now = new Date();
  const dateTodayFormatted = now.getFullYear() + "-" +
    String(now.getMonth() + 1).padStart(2, "0") + "-" +
    String(now.getDate()).padStart(2, "0");
  
  // Fonction utilitaire pour formater une date string en YYYY-MM-DD locale (nettoyage suffixe ordinal)
  const formatDateStringToLocalYMD = (dateStr) => {
    if (!dateStr) return null;
    // Nettoyer suffixes (1st, 2nd, 3rd, 4th...)
    const cleanedStr = dateStr.replace(/(\d+)(st|nd|rd|th)/, '$1');
    const parsedDate = new Date(cleanedStr);
    if (isNaN(parsedDate)) return null;

    return parsedDate.getFullYear() + "-" +
      String(parsedDate.getMonth() + 1).padStart(2, "0") + "-" +
      String(parsedDate.getDate()).padStart(2, "0");
  };
  
  // 📊 Vérifier que les données correspondent au jour actuel
  if (queueData.today) {
    const formattedToday = formatDateStringToLocalYMD(queueData.today);
    
    if (!formattedToday || formattedToday !== dateTodayFormatted) {
      console.warn(`⚠️ [KPI Parse] Queue ${queueData.queueId || queueData.id} - Date mismatch! Expected: ${dateTodayFormatted}, Got: ${formattedToday}`);
      
      // Si la date ne correspond pas au jour actuel, retourner des KPI à 0
      return {
        queueId: queueData.queueId || queueData.id || queueData.RowKey,
        queueName: queueData.queueName || queueData.name || queueData.RowKey,
        scope: queueData.scope || queueData.PartitionKey,
        todayCalls: 0,
        callsInQueue: 0,
        liveCalls: 0,
        tasksInQueue: 0,
        agentsReady: 0,
        averageWaitTime: 0,
        avgWaitTime: "00:00:00",
        longestWait: 0,
        maxWaitTime: "00:00:00",
        sla: 0,
        slaReached: 0,
        abandonRate: 0,
        overflowRate: 0,
        abandonedCalls: 0,
        queueFallback: 0,
        todayChats: 0,
        slaChat: 0,
        averageWaitTimeChat: 0,
        longestWaitChat: 0,
        slaReachedChat: 0,
        agents: {
          ready: 0,
          busy: 0,
          coffeeBreak: 0,
          adminWork: 0,
          outOfShift: 0,
          offline: 0,
          total: 0
        },
        agentDetails: queueData.agentDetails || {},
        reasonCodes: queueData.reasonCodes || []
      };
    }
  }
  
  console.log(`✅ [KPI Parse] Queue ${queueData.queueId || queueData.id} - Date OK: ${dateTodayFormatted}`, {
    todayQueuedCalls: queueData.todayQueuedCalls,
    abandonedCall: queueData.abandonedCall,
    slaReached: queueData.slaReached
  });
  
  // Extraire les propriétés de la queue (même logique que QueueComponent)
  const {
    calls,
    tasks,
    agentDetails,
    todayQueuedCalls,
    abandonedCall,
    queueFallback,
    slaReached,
    averageQueueingTime,
    longestWait,
    chatStats,
  } = queueData;

  // Calculer callsInQueue (appels en cours / live calls)
  const callsInQueue = Array.isArray(calls)
    ? calls.length
    : calls
    ? Object.keys(calls).length
    : 0;

  // Calculer tasksInQueue (chats/messages en cours)
  const tasksInQueue = tasks ? Object.keys(tasks).length : 0;

  // Calculer agentsReady
  const agentsReady = agentDetails && typeof agentDetails === "object"
    ? Object.values(agentDetails).filter((agent) => agent.state === "READY").length
    : 0;

  // Gérer les statistiques de chat
  let todayChats = 0;
  let averageWaitTimeChat = 0;
  let longestWaitChat = 0;
  let slaChat = 0;
  let slaReachedChat = 0;

  if (chatStats) {
    todayChats = chatStats.todayQueuedChats || 0;
    averageWaitTimeChat = chatStats.averageWaitTime || 0;
    longestWaitChat = chatStats.longestWait || 0;
    slaReachedChat = chatStats.slaReachedChat || 0;
    slaChat = chatStats.todayQueuedChats && slaReachedChat
      ? (slaReachedChat / chatStats.todayQueuedChats) * 100
      : 0;
  }

  // Calculer les métriques principales
  const todayCalls = todayQueuedCalls || 0;
  const averageWaitTime = averageQueueingTime || 0;
  const abandonRate = todayQueuedCalls && abandonedCall
    ? (abandonedCall / todayQueuedCalls) * 100
    : 0;
  const overflowRate = todayQueuedCalls && queueFallback
    ? (queueFallback / todayQueuedCalls) * 100
    : 0;
  const sla = todayQueuedCalls && slaReached
    ? (slaReached / todayQueuedCalls) * 100
    : 0;

  // Compter les agents par état depuis agentDetails + reasonCodes
  const agentStates = {};
  
  // Initialiser les états de base
  agentStates.ready = 0;
  agentStates.busy = 0;
  agentStates.coffeeBreak = 0;
  agentStates.adminWork = 0;
  agentStates.outOfShift = 0;
  agentStates.offline = 0;
  
  // Compter les reason codes depuis la liste reasonCodes
  const reasonCodes = queueData.reasonCodes || [];
  reasonCodes.forEach((code) => {
    if (code) {
      if (!agentStates[code]) {
        agentStates[code] = 0;
      }
      agentStates[code]++;
    }
  });
  
  // Compter les états des agents depuis agentDetails
  Object.values(agentDetails || {}).forEach((agent) => {
    const state = agent.state?.toUpperCase() || 'OFFLINE';
    
    if (state === 'READY') {
      agentStates.ready++;
    } else if (state === 'BUSY' || state === 'ONCALL') {
      agentStates.busy++;
    } else if (state.includes('BREAK') || state.includes('COFFEE')) {
      agentStates.coffeeBreak++;
    } else if (state.includes('ADMIN')) {
      agentStates.adminWork++;
    } else if (state.includes('OUTOFSHIFT') || state === 'OUT OF SHIFT') {
      agentStates.outOfShift++;
    } else {
      // Si c'est un reason code personnalisé, l'incrémenter
      if (agentStates[state] !== undefined) {
        agentStates[state]++;
      } else {
        agentStates.offline++;
      }
    }
  });
  
  // Calculer le total
  agentStates.total = Object.values(agentDetails || {}).length;

  return {
    queueId: queueData.queueId || queueData.id || queueData.RowKey,
    queueName: queueData.queueName || queueData.name || queueData.RowKey,
    scope: queueData.scope || queueData.PartitionKey,
    
    // KPI principaux (noms cohérents avec QueueComponent)
    todayCalls: todayCalls,
    callsInQueue: callsInQueue,  // appels en cours (live calls)
    liveCalls: callsInQueue,     // alias pour compatibilité
    tasksInQueue: tasksInQueue,
    agentsReady: agentsReady,
    
    // Temps d'attente
    averageWaitTime: averageWaitTime,
    avgWaitTime: secondsToTimeString(averageWaitTime),  // Format HH:MM:SS
    longestWait: longestWait || 0,
    maxWaitTime: secondsToTimeString(longestWait || 0), // Format HH:MM:SS
    
    // Taux et SLA
    sla: sla,
    slaReached: slaReached || 0,
    abandonRate: abandonRate,
    overflowRate: overflowRate,
    abandonedCalls: abandonedCall || 0,
    queueFallback: queueFallback || 0,
    
    // Statistiques de chat
    todayChats: todayChats,
    slaChat: slaChat,
    averageWaitTimeChat: averageWaitTimeChat,
    longestWaitChat: longestWaitChat,
    slaReachedChat: slaReachedChat,

    // États des agents
    agents: agentStates,

    // Détails des agents (pour la liste avec leurs infos complètes)
    agentDetails: agentDetails || {},
    
    // Codes de raison / activités des agents
    reasonCodes: queueData.reasonCodes || []
  };
}

/**
 * Calcule les KPI totaux à partir d'un objet de KPI par queue
 * @param {Object} queueKpis - Objet {queueId: kpi}
 * @returns {Object} - KPI totaux
 */
export function calculateTotalKpi(queueKpis) {
  const queues = Object.values(queueKpis);
  if (queues.length === 0) {
    return {
      kpi: {
        todayCalls: 0,
        callsInQueue: 0,
        liveCalls: 0,
        tasksInQueue: 0,
        agentsReady: 0,
        avgWaitTime: "00:00:00",
        maxWaitTime: "00:00:00",
        longestWait: 0,
        averageWaitTime: 0,
        sla: 0,
        abandonRate: 0,
        overflowRate: 0,
        abandonedCalls: 0,
        queueFallback: 0
      },
      agents: {
        ready: 0,
        busy: 0,
        coffeeBreak: 0,
        adminWork: 0,
        outOfShift: 0,
        offline: 0,
        total: 0
      },
      queueCount: 0
    };
  }

  // Somme des valeurs numériques simples
  const totals = queues.reduce((acc, queue) => {
    // Gérer les temps d'attente (en secondes)
    const avgWaitSeconds = queue.averageWaitTime || timeStringToSeconds(queue.avgWaitTime);
    const maxWaitSeconds = queue.longestWait || timeStringToSeconds(queue.maxWaitTime);
    
    return {
      todayCalls: acc.todayCalls + (queue.todayCalls || 0),
      callsInQueue: acc.callsInQueue + (queue.callsInQueue || 0),
      tasksInQueue: acc.tasksInQueue + (queue.tasksInQueue || 0),
      agentsReady: acc.agentsReady + (queue.agentsReady || 0),
      abandonedCalls: acc.abandonedCalls + (queue.abandonedCalls || 0),
      queueFallback: acc.queueFallback + (queue.queueFallback || 0),
      slaReached: acc.slaReached + (queue.slaReached || 0),
      
      ready: acc.ready + (queue.agents?.ready || 0),
      busy: acc.busy + (queue.agents?.busy || 0),
      coffeeBreak: acc.coffeeBreak + (queue.agents?.coffeeBreak || 0),
      adminWork: acc.adminWork + (queue.agents?.adminWork || 0),
      outOfShift: acc.outOfShift + (queue.agents?.outOfShift || 0),
      offline: acc.offline + (queue.agents?.offline || 0),
      total: acc.total + (queue.agents?.total || 0),
      
      // Pour les temps, on accumule les secondes (seulement si > 0)
      avgWaitSeconds: avgWaitSeconds > 0 ? acc.avgWaitSeconds + avgWaitSeconds : acc.avgWaitSeconds,
      avgWaitCount: avgWaitSeconds > 0 ? acc.avgWaitCount + 1 : acc.avgWaitCount,
      maxWaitSeconds: Math.max(acc.maxWaitSeconds, maxWaitSeconds),
      
      // Pour le SLA, on accumule pour faire une moyenne pondérée
      slaSum: acc.slaSum + (queue.sla || 0),
      slaCount: acc.slaCount + 1
    };
  }, {
    todayCalls: 0, callsInQueue: 0, tasksInQueue: 0, agentsReady: 0,
    abandonedCalls: 0, queueFallback: 0, slaReached: 0,
    ready: 0, busy: 0, coffeeBreak: 0, adminWork: 0,
    outOfShift: 0, offline: 0, total: 0,
    avgWaitSeconds: 0, avgWaitCount: 0, maxWaitSeconds: 0,
    slaSum: 0, slaCount: 0
  });

  // Calculer la moyenne du temps d'attente (seulement pour les queues avec temps > 0)
  const avgWaitTime = totals.avgWaitCount > 0 
    ? secondsToTimeString(totals.avgWaitSeconds / totals.avgWaitCount)
    : "00:00:00";
  const maxWaitTime = secondsToTimeString(totals.maxWaitSeconds);
  const avgSla = totals.slaCount > 0 ? Math.round(totals.slaSum / totals.slaCount) : 0;
  
  // Recalculer les rates basés sur les totaux (comme DashboardV2)
  const abandonRate = totals.todayCalls > 0 
    ? (totals.abandonedCalls / totals.todayCalls) * 100 
    : 0;
  const overflowRate = totals.todayCalls > 0 
    ? (totals.queueFallback / totals.todayCalls) * 100 
    : 0;
  const sla = totals.todayCalls > 0 
    ? (totals.slaReached / totals.todayCalls) * 100 
    : 0;

  return {
    kpi: {
      todayCalls: totals.todayCalls,
      callsInQueue: totals.callsInQueue,
      liveCalls: totals.callsInQueue,  // alias pour compatibilité
      tasksInQueue: totals.tasksInQueue,
      agentsReady: totals.agentsReady,
      avgWaitTime: avgWaitTime,
      maxWaitTime: maxWaitTime,
      longestWait: totals.maxWaitSeconds,
      averageWaitTime: totals.avgWaitCount > 0 
        ? Math.round(totals.avgWaitSeconds / totals.avgWaitCount)
        : 0,
      sla: parseFloat(sla.toFixed(2)),
      slaReached: totals.slaReached,
      abandonedCalls: totals.abandonedCalls,
      queueFallback: totals.queueFallback,
      abandonRate: parseFloat(abandonRate.toFixed(2)),
      overflowRate: parseFloat(overflowRate.toFixed(2))
    },
    agents: {
      ready: totals.ready,
      busy: totals.busy,
      coffeeBreak: totals.coffeeBreak,
      adminWork: totals.adminWork,
      outOfShift: totals.outOfShift,
      offline: totals.offline,
      total: totals.total
    },
    queueCount: queues.length
  };
}

/**
 * Convertit une chaîne de temps "HH:MM:SS" en secondes
 * @param {string} timeString - Temps au format "HH:MM:SS"
 * @returns {number} - Nombre de secondes
 */
function timeStringToSeconds(timeString) {
  if (!timeString || timeString === "--") return 0;
  
  const parts = timeString.split(':');
  if (parts.length !== 3) return 0;
  
  const hours = parseInt(parts[0], 10) || 0;
  const minutes = parseInt(parts[1], 10) || 0;
  const seconds = parseInt(parts[2], 10) || 0;
  
  return hours * 3600 + minutes * 60 + seconds;
}

/**
 * Convertit un nombre de secondes en chaîne "HH:MM:SS"
 * @param {number} totalSeconds - Nombre de secondes
 * @returns {string} - Temps au format "HH:MM:SS"
 */
function secondsToTimeString(totalSeconds) {
  if (!totalSeconds || totalSeconds === 0) return "00:00:00";
  
  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = Math.floor(totalSeconds % 60);
  
  return `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`;
}

/**
 * Extrait tous les agents des queues sélectionnées
 * @param {Object} queueKpis - Objet {queueId: kpi}
 * @param {Object} agentsData - Données complètes des agents depuis Redux
 * @returns {Array} - Liste des agents avec leurs informations
 */
export function extractAgentsFromQueues(queueKpis, agentsData) {
  const agentsMap = new Map();

  Object.values(queueKpis).forEach(queue => {
    if (queue.agentDetails) {
      Object.entries(queue.agentDetails).forEach(([agentId, agentData]) => {
        const agentInfo = agentsData?.[agentId] || {};
        
        // Essayer plusieurs propriétés pour trouver le nom de l'agent
        const agentName = agentInfo.name || 
                          agentInfo.displayName || 
                          agentInfo.fullName || 
                          agentInfo.userName || 
                          agentInfo.email || 
                          agentId;

        agentsMap.set(agentId, {
          id: agentId,
          name: agentName,
          state: agentData.state || "OFFLINE",
          queueName: queue.queueName,
          queueId: queue.queueId,
          ...agentData
        });
      });
    }
  });

  return Array.from(agentsMap.values());
}


// ------------------ REALTIME DASHBOARD LAYOUT ------------------


export async function saveLayoutPositions(tenantId, layoutPositions, token) {
  try {
    // Add layout to realtimeDashboard section
    const portalSettings = {
      realtimeDashboard: {
        layoutSettings: {
          grid: layoutPositions
        }
      }
    };
    
    // Use add endpoint to merge with existing settings
    return await addPortalSettings(tenantId, portalSettings, token);
  } catch (error) {
    console.error('Error saving layout positions:', error);
    return {
      success: false,
      message: error.message
    };
  }
}


export async function getLayoutPositions(tenantId, token) {
  try {
    const result = await getPortalSettings(tenantId, token);
    
    if (result.success && result.data) {
      // Extract grid layout from portal settings
      const gridLayout = result.data?.portalSettting?.realtimeDashboard?.layoutSettings?.grid;
      return {
        success: true,
        data: gridLayout || null
      };
    }
    
    return {
      success: true,
      data: null // No saved layout, use default
    };
  } catch (error) {
    console.error('Error getting layout positions:', error);
    return {
      success: false,
      data: null,
      message: error.message
    };
  }
}
